import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCustomers } from '@hooks/useAdminResources';
import { useCustomerBookings } from '@hooks/useBookings';
import { adminCustomersApi } from '@api/client';
import type { Customer } from '@api/types';
import * as Icons from '@components/Icons';

// Editable fields the operator picks per-side. Keys match the backend's
// `overrides` shape in `merge-customers.usecase.ts`.
type FieldKey = 'name' | 'email' | 'phone' | 'internalNote';
type Side = 'target' | 'source';
type Picks = Record<FieldKey, Side>;

const FIELD_DEFS: { key: FieldKey; label: string; mono?: boolean }[] = [
  { key: 'name',         label: 'Nombre' },
  { key: 'email',        label: 'Email',    mono: true },
  { key: 'phone',        label: 'Teléfono', mono: true },
  { key: 'internalNote', label: 'Nota' },
];

function valueOf(c: Customer, key: FieldKey): string {
  if (key === 'internalNote') return c.internalNote ?? '';
  return c[key];
}

function ChooserRow({ field, target, source, pick, onChange }: {
  field: { key: FieldKey; label: string; mono?: boolean };
  target: Customer;
  source: Customer;
  pick: Side;
  onChange: (s: Side) => void;
}) {
  const tVal = valueOf(target, field.key);
  const sVal = valueOf(source, field.key);
  const sameValue = tVal === sVal;
  const valueClass = field.mono ? 'mono' : '';
  const radioName = `merge-${field.key}`;

  if (sameValue) {
    return (
      <div style={{ padding: '8px 0', borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ width: 90, color: 'var(--fg-soft)', fontSize: 12, fontWeight: 500 }}>
            {field.label}
          </span>
          <span className={valueClass} style={{ fontSize: 13, flex: 1 }}>
            {tVal || <span style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>—</span>}
          </span>
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', fontStyle: 'italic' }}>
            igual en ambos
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ color: 'var(--fg-soft)', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
        {field.label}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {(['target', 'source'] as const).map((side) => {
          const val = side === 'target' ? tVal : sVal;
          const checked = pick === side;
          return (
            <label
              key={side}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                border: checked ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: checked ? 'var(--accent-soft)' : 'transparent',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name={radioName}
                checked={checked}
                onChange={() => { onChange(side); }}
                style={{ marginTop: 2, accentColor: 'var(--accent)' }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className={valueClass} style={{ wordBreak: 'break-word' }}>
                  {val || <span style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>—</span>}
                </div>
                <div style={{
                  fontSize: 10.5,
                  letterSpacing: '0.02em',
                  marginTop: 4,
                  color: side === 'target' ? 'var(--accent-dark)' : 'var(--fg-muted)',
                  fontWeight: 600,
                }}>
                  {side === 'target' ? 'PRINCIPAL' : 'DUPLICADO'}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function MergePicker({ target, source, picks, setPicks, hasError }: {
  target: Customer;
  source: Customer;
  picks: Picks;
  setPicks: (next: Picks) => void;
  hasError: boolean;
}) {
  // Live count from the source's active bookings — same query the customer
  // detail uses, so it stays in sync with whatever paging shows.
  const { data: bookingsPage } = useCustomerBookings(source.id, 1);
  const bookingsCount = bookingsPage?.total ?? source.bookingsCount ?? 0;

  return (
    <div>
      <p style={{ fontSize: 13, marginBottom: 12 }}>
        Vas a fusionar <strong>{source.name}</strong>{' '}
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 400 }}>#{source.id.slice(0, 8)}</span>{' '}
        (duplicado) dentro de <strong>{target.name}</strong>{' '}
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 400 }}>#{target.id.slice(0, 8)}</span>{' '}
        (principal). Elige qué dato conservar para cada campo:
      </p>

      <div style={{ marginBottom: 14 }}>
        {FIELD_DEFS.map((field) => (
          <ChooserRow
            key={field.key}
            field={field}
            target={target}
            source={source}
            pick={picks[field.key]}
            onChange={(s) => { setPicks({ ...picks, [field.key]: s }); }}
          />
        ))}
      </div>

      <div style={{
        padding: '10px 12px',
        background: 'var(--bg-soft)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12.5,
        lineHeight: 1.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontWeight: 600, fontSize: 13 }}>
          <Icons.Calendar size={13} /> Reservas
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--fg-muted)' }}>
          <li>
            <strong style={{ color: 'var(--fg)' }}>{bookingsCount}</strong> reserva{bookingsCount === 1 ? '' : 's'}{' '}
            del duplicado se mueven al principal con su mismo profesional, sala y horario.
          </li>
          <li>El cliente duplicado <strong style={{ color: 'var(--fg)' }}>se elimina</strong> y desaparece de la lista.</li>
        </ul>
      </div>

      <p style={{ fontSize: 12, color: 'var(--accent-dark)', marginTop: 12, fontWeight: 600 }}>
        Esto no se puede deshacer.
      </p>

      {hasError && (
        <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 10 }}>
          No se pudo fusionar. Inténtalo de nuevo.
        </div>
      )}
    </div>
  );
}

export function MergeCustomerModal({
  target,
  onClose,
}: {
  target: Customer;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [selectedSource, setSelectedSource] = useState<Customer | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Default: target wins for every field — matches the backend default for
  // an absent `overrides` block and the historical behavior.
  const [picks, setPicks] = useState<Picks>({
    name: 'target', email: 'target', phone: 'target', internalNote: 'target',
  });

  // Reuse the same listing endpoint with `q` for typeahead. Skip the search
  // entirely when q is empty to avoid loading the full customer list.
  const { data } = useCustomers(q.trim().length >= 2 ? { q: q.trim() } : {});

  const candidates = useMemo(() => {
    const items: Customer[] = data?.data ?? [];
    // Don't show the target itself in the list.
    return items.filter((c) => c.id !== target.id).slice(0, 12);
  }, [data, target.id]);

  const mutation = useMutation({
    mutationFn: () => {
      // Trim to only the fields actually overridden so the audit log + payload
      // reflect explicit operator intent rather than defaults.
      const overrides: Partial<Record<FieldKey, Side>> = {};
      for (const k of Object.keys(picks) as FieldKey[]) {
        if (picks[k] === 'source') overrides[k] = 'source';
      }
      const overridesArg = Object.keys(overrides).length > 0 ? overrides : undefined;
      return adminCustomersApi.merge(target.id, selectedSource?.id ?? '', overridesArg);
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      window.alert(`Fusión completada. ${String(result.bookingsMoved)} reservas movidas.`);
      onClose();
    },
  });

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => { e.stopPropagation(); }} style={{ maxWidth: 540 }}>
        <div className="modal-head">
          <div className="modal-title">Fusionar clientes</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <div className="modal-body">
          {!confirming && (
            <>
              <div
                style={{
                  padding: '10px 12px',
                  background: 'var(--bg-soft)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  marginBottom: 14,
                }}
              >
                Vas a fusionar dos clientes. Busca y selecciona el segundo cliente; en el siguiente paso
                eliges <strong>qué datos conservar de cada uno</strong>. Las reservas del segundo cliente se
                moverán a <strong>{target.name}</strong>{' '}
                <span className="mono">#{target.id.slice(0, 8)}</span>, y el segundo queda eliminado tras la
                fusión.
              </div>

              <div className="field">
                <label className="field-label">Buscar segundo cliente</label>
                <input
                  className="input"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); }}
                  placeholder="Nombre o email…"
                  autoFocus
                />
              </div>

              {candidates.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
                  {candidates.map((c) => {
                    const active = selectedSource?.id === c.id;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => { setSelectedSource(c); }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 10px',
                            border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
                            background: active ? 'var(--bg-soft)' : 'transparent',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 13,
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>
                            {c.name} <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 400 }}>#{c.id.slice(0, 8)}</span>
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                            {c.email} · <span className="mono">{c.phone}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {q.trim().length >= 2 && candidates.length === 0 && (
                <p style={{ fontSize: 12.5, color: 'var(--fg-soft)', fontStyle: 'italic' }}>
                  Ningún cliente coincide.
                </p>
              )}
            </>
          )}

          {confirming && selectedSource && (
            <MergePicker
              target={target}
              source={selectedSource}
              picks={picks}
              setPicks={setPicks}
              hasError={mutation.isError}
            />
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={mutation.isPending}>Cancelar</button>
          {!confirming ? (
            <button
              className="btn"
              data-variant="primary"
              disabled={!selectedSource}
              onClick={() => { setConfirming(true); }}
            >
              Continuar
            </button>
          ) : (
            <button
              className="btn"
              data-variant="primary"
              disabled={mutation.isPending}
              onClick={() => { mutation.mutate(); }}
            >
              {mutation.isPending ? 'Fusionando…' : 'Fusionar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
